import { useHandler } from "@redotech/react-util/hook";
import {
  AfterTimeframeCondition,
  AnnualTimeframeCondition,
  annualTimeframeConditionTypes,
  BeforeNowRelativeTimeframeCondition,
  BeforeTimeframeCondition,
  BetweenDatesTimeframeCondition,
  OnTimeframeCondition,
  TimeframeConditionType,
  TimeframeUnit,
} from "@redotech/redo-model/marketing/segments/segment-timeframe";
import {
  dateToPlainDateTime,
  plainDateTimeToDate,
} from "@redotech/util/temporal";
import { assertNever } from "@redotech/util/type";
import { FC, memo, useContext, useMemo } from "react";
import { RedoDateRangePicker } from "../../../arbiter-components/date-picker/redo-date-range-picker";
import { RedoSingleDatePicker } from "../../../arbiter-components/date-picker/redo-single-date-picker";
import { RedoListItem } from "../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  SegmentBuilderLocationContext,
  SegmentEditorLocation,
} from "../segment-builder-editor";
import { AnnualTimeframeConditionInProgress as InProgress } from "../segment-in-progress";
import { NumericFilterInput } from "./numeric-filter";

export const AnnualDateTimeframeFilter = memo(
  function AnnualDateTimeframeFilter({
    comparison,
    setComparison,
  }: {
    comparison: InProgress<AnnualTimeframeCondition>;
    setComparison: (comparison: InProgress<AnnualTimeframeCondition>) => void;
  }) {
    const handleConditionTypeChange = useHandler(
      (conditionType: AnnualTimeframeCondition["type"]) => {
        if (conditionType === comparison.type) {
          return;
        }
        const defaultValues = conditionTypeToDefault[conditionType];
        setComparison(defaultValues);
      },
    );

    const TimeFrameConfiguration: FC<{
      condition: any;
      setCondition: (condition: any) => void;
    }> | null = useMemo(() => {
      switch (comparison.type) {
        case TimeframeConditionType.ON:
          return OnTimeFrameSelector;
        case TimeframeConditionType.BEFORE_NOW_RELATIVE:
          return BeforeNowRelativeTimeFrameSelector;
        case TimeframeConditionType.BEFORE:
          return BeforeTimeFrameSelector;
        case TimeframeConditionType.AFTER:
          return AfterTimeFrameSelector;
        case TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT:
          return null;
        case TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT:
          return null;
        case TimeframeConditionType.TODAY:
          return null;
        case TimeframeConditionType.BETWEEN_DATES:
          return BetweenDatesTimeFrameSelector;
        case TimeframeConditionType.THIS_WEEK:
          return null;
        case TimeframeConditionType.THIS_MONTH:
          return null;
        default:
          assertNever(comparison);
      }
    }, [comparison]);

    return (
      <>
        <AnnualDateTimeFrameConditionTypeSelector
          conditionType={comparison.type}
          setConditionType={handleConditionTypeChange}
        />
        {TimeFrameConfiguration && (
          <TimeFrameConfiguration
            condition={comparison}
            setCondition={setComparison}
          />
        )}
      </>
    );
  },
);

const conditionTypeToDefault: Record<
  AnnualTimeframeCondition["type"],
  InProgress<AnnualTimeframeCondition>
> = {
  [TimeframeConditionType.BEFORE_NOW_RELATIVE]: {
    type: TimeframeConditionType.BEFORE_NOW_RELATIVE,
    units: TimeframeUnit.DAY,
  },
  [TimeframeConditionType.AFTER]: { type: TimeframeConditionType.AFTER },
  [TimeframeConditionType.BEFORE]: { type: TimeframeConditionType.BEFORE },
  [TimeframeConditionType.BETWEEN_DATES]: {
    type: TimeframeConditionType.BETWEEN_DATES,
  },
  [TimeframeConditionType.ON]: { type: TimeframeConditionType.ON },
  [TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT]: {
    type: TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT,
    units: TimeframeUnit.DAY,
  },
  [TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT]: {
    type: TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT,
    units: TimeframeUnit.DAY,
  },
  [TimeframeConditionType.TODAY]: { type: TimeframeConditionType.TODAY },
  [TimeframeConditionType.THIS_WEEK]: {
    type: TimeframeConditionType.THIS_WEEK,
  },
  [TimeframeConditionType.THIS_MONTH]: {
    type: TimeframeConditionType.THIS_MONTH,
  },
};

const timeFrameConditionTypeToFriendly: Record<
  AnnualTimeframeCondition["type"],
  string
> = {
  [TimeframeConditionType.BEFORE_NOW_RELATIVE]: "in the last",
  [TimeframeConditionType.AFTER]: "after",
  [TimeframeConditionType.BEFORE]: "before",
  [TimeframeConditionType.BETWEEN_DATES]: "between dates",
  [TimeframeConditionType.ON]: "on",
  [TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT]: "before",
  [TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT]: "after",
  [TimeframeConditionType.TODAY]: "today",
  [TimeframeConditionType.THIS_WEEK]: "this week",
  [TimeframeConditionType.THIS_MONTH]: "this month",
};

const allAnnualDateTimeFrameConditionTypeOptions: RedoListItem<
  AnnualTimeframeCondition["type"]
>[] = annualTimeframeConditionTypes
  // If we want these condition types in segments, we'll have to move
  // some stuff around to make the sentence make sense. "5 days ago" vs. "ago 5 days"
  .filter(
    (type) =>
      ![
        TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT,
        TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT,
      ].includes(type),
  )
  .map((type) => ({
    id: type,
    type: "text",
    text: timeFrameConditionTypeToFriendly[type],
    value: type,
  }));

const AnnualDateTimeFrameConditionTypeSelector = memo(
  function AnnualDateTimeFrameConditionTypeSelector({
    conditionType,
    setConditionType,
  }: {
    conditionType: AnnualTimeframeCondition["type"];
    setConditionType: (conditionType: AnnualTimeframeCondition["type"]) => void;
  }) {
    const location = useContext(SegmentBuilderLocationContext);

    const timeframeOptions: RedoListItem<AnnualTimeframeCondition["type"]>[] =
      useMemo(() => {
        switch (location) {
          case SegmentEditorLocation.SEGMENT_BUILDER:
            return allAnnualDateTimeFrameConditionTypeOptions;
          case SegmentEditorLocation.AUTOMATIONS_PANEL:
            return allAnnualDateTimeFrameConditionTypeOptions.filter(
              (option) => option.value !== TimeframeConditionType.TODAY,
            );
          default:
            assertNever(location);
        }
      }, [location]);

    return (
      <RedoSingleSelectDropdownInput<AnnualTimeframeCondition["type"]>
        fitToAnchor={false}
        flexGrow={false}
        options={timeframeOptions}
        optionSelected={({ item }) => setConditionType(item.value)}
        selectedItem={allAnnualDateTimeFrameConditionTypeOptions.find(
          (o) => o.id === conditionType,
        )}
        size="xs"
      />
    );
  },
);

const BeforeTimeFrameSelector = memo(function BeforeTimeFrameSelector({
  condition,
  setCondition,
}: {
  condition: InProgress<BeforeTimeframeCondition>;
  setCondition: (condition: InProgress<BeforeTimeframeCondition>) => void;
}) {
  return (
    <RedoSingleDatePicker
      buttonSize="sm"
      date={
        condition.date
          ? // The interface for timeframe conditions says date is a Date,
            // but that's not the case in the automation builder because the
            // zod schema for automations uses z.any() for inline segments.
            // Better solution would be to fill out that schema.
            dateToPlainDateTime(new Date(condition.date)).toPlainDate()
          : undefined
      }
      dateStringOptions={{ year: undefined }}
      setDate={(plainDate) => {
        setCondition({
          ...condition,
          date: plainDateTimeToDate(plainDate.toPlainDateTime()),
        });
      }}
    />
  );
});

const AfterTimeFrameSelector = memo(function AfterTimeFrameSelector({
  condition,
  setCondition,
}: {
  condition: InProgress<AfterTimeframeCondition>;
  setCondition: (condition: InProgress<AfterTimeframeCondition>) => void;
}) {
  return (
    <RedoSingleDatePicker
      buttonSize="sm"
      date={
        condition.date
          ? dateToPlainDateTime(new Date(condition.date)).toPlainDate()
          : undefined
      }
      dateStringOptions={{ year: undefined }}
      maxDate={dateToPlainDateTime(new Date()).toPlainDate()}
      setDate={(plainDate) => {
        setCondition({
          ...condition,
          date: plainDateTimeToDate(plainDate.toPlainDateTime()),
        });
      }}
    />
  );
});

const BetweenDatesTimeFrameSelector = memo(
  function BetweenDatesTimeFrameSelector({
    condition,
    setCondition,
  }: {
    condition: InProgress<BetweenDatesTimeframeCondition>;
    setCondition: (
      condition: InProgress<BetweenDatesTimeframeCondition>,
    ) => void;
  }) {
    return (
      <RedoDateRangePicker
        dateRange={
          condition.range
            ? [
                dateToPlainDateTime(new Date(condition.range[0])).toPlainDate(),
                dateToPlainDateTime(new Date(condition.range[1])).toPlainDate(),
              ]
            : undefined
        }
        dateStringOptions={{ year: undefined }}
        setDateRange={(range) => {
          setCondition({
            ...condition,
            range: range
              ? [
                  plainDateTimeToDate(range[0].toPlainDateTime()),
                  plainDateTimeToDate(range[1].toPlainDateTime()),
                ]
              : undefined,
          });
        }}
        size="sm"
      />
    );
  },
);

const OnTimeFrameSelector = memo(function OnTimeFrameSelector({
  condition,
  setCondition,
}: {
  condition: InProgress<OnTimeframeCondition>;
  setCondition: (condition: InProgress<OnTimeframeCondition>) => void;
}) {
  return (
    <RedoSingleDatePicker
      buttonSize="sm"
      date={
        condition.date
          ? dateToPlainDateTime(new Date(condition.date)).toPlainDate()
          : undefined
      }
      dateStringOptions={{ year: undefined }}
      setDate={(plainDate) => {
        setCondition({
          ...condition,
          date: plainDateTimeToDate(plainDate.toPlainDateTime()),
        });
      }}
    />
  );
});

const BeforeNowRelativeTimeFrameSelector = memo(
  function BeforeRelativeTimeFrameSelector({
    condition,
    setCondition,
  }: {
    condition: InProgress<BeforeNowRelativeTimeframeCondition>;
    setCondition: (
      condition: InProgress<BeforeNowRelativeTimeframeCondition>,
    ) => void;
  }) {
    const { value, units } = condition;

    return (
      <>
        <TimeframeNumericInput
          setValue={(number) => {
            setCondition({ ...condition, value: number });
          }}
          value={value}
        />
        <TimeframeUnitSelector
          isPlural={value !== 1}
          setTimeframeUnit={(unit) =>
            setCondition({ ...condition, units: unit })
          }
          timeframeUnit={units}
        />
      </>
    );
  },
);

const timeFrameUnitToFriendlySingular: Record<TimeframeUnit, string> = {
  [TimeframeUnit.HOUR]: "hour",
  [TimeframeUnit.DAY]: "day",
  [TimeframeUnit.WEEK]: "week",
  [TimeframeUnit.MONTH]: "month",
};

const timeFrameUnitToFriendlyPlural: Record<TimeframeUnit, string> = {
  [TimeframeUnit.HOUR]: "hours",
  [TimeframeUnit.DAY]: "days",
  [TimeframeUnit.WEEK]: "weeks",
  [TimeframeUnit.MONTH]: "months",
};

const TimeframeUnitSelector = memo(function TimeframeUnitSelector({
  isPlural,
  timeframeUnit,
  setTimeframeUnit,
}: {
  isPlural: boolean;
  timeframeUnit: TimeframeUnit | undefined;
  setTimeframeUnit: (unit: TimeframeUnit) => void;
}) {
  const timeFrameUnitOptions: RedoListItem<TimeframeUnit>[] = useMemo(
    () =>
      Object.values(TimeframeUnit)
        .filter((unit) => unit !== TimeframeUnit.HOUR)
        .map((unit) => ({
          id: unit,
          type: "text",
          text: isPlural
            ? timeFrameUnitToFriendlyPlural[unit]
            : timeFrameUnitToFriendlySingular[unit],
          value: unit,
        })),
    [isPlural],
  );

  return (
    <RedoSingleSelectDropdownInput<TimeframeUnit>
      fitToAnchor={false}
      flexGrow={false}
      options={timeFrameUnitOptions}
      optionSelected={({ item }) => setTimeframeUnit(item.value)}
      selectedItem={timeFrameUnitOptions.find((o) => o.id === timeframeUnit)}
      size="xs"
    />
  );
});

const TimeframeNumericInput = memo(function TimeframeNumericInput({
  value,
  setValue,
}: {
  value: number | undefined;
  setValue: (value: number | undefined) => void;
}) {
  return <NumericFilterInput maxDigits={3} setValue={setValue} value={value} />;
});
